import { useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useState, type ReactElement } from 'react';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import type { MeResponse } from '@platform/shared';
import { CALENDAR_PATH, entityPath, FINDINGS_INBOX_PATH, GATES_PATH, NAV, ownerOf, parseFrom, PHONE_MORE, PHONE_TABS, type NavId, type NavItem } from '@platform/shared';
import { api } from '../api/client';
import { AssistButton } from '../components/assist/AssistButton';
import { AssistDrawer } from '../components/assist/AssistDrawer';
import { AssistProvider, useAssistShell } from '../components/assist/AssistProvider';
import { LazyHandoffSheet } from '../components/LazyHandoffSheet';
import { LocaleSwitcher } from '../components/LocaleSwitcher';
import { OfflineBanner } from '../components/OfflineBanner';
import { Sheet } from '../components/Sheet';
import { Tooltip } from '../components/Tooltip';
import { useReportWidget } from '../components/report/useReportWidget';
import { common, ENTITY_LABEL, roles, rooms, routes as routeTitles } from '../i18n/messages';
import { ActionsProvider, TopbarActionsSlot } from './actions';
import { CrumbsProvider, usePageCrumbs, type Crumb } from './crumbs';
import { BrandMark, BuildIcon, DocsIcon, HandoffIcon, MoreIcon, PlanIcon, PulseIcon, StoriesIcon, TracksIcon, WireframesIcon, WorkspaceIcon, YouIcon } from './icons';

type Icon = (p: { className?: string }) => ReactElement;
const NAV_ICONS: Record<NavId, Icon> = {
  pulse: PulseIcon,
  plan: PlanIcon,
  tracks: TracksIcon,
  stories: StoriesIcon,
  wireframes: WireframesIcon,
  docs: DocsIcon,
  workspace: WorkspaceIcon,
  build: BuildIcon,
  you: YouIcon,
};

type RoutePath = keyof typeof routeTitles;

type ShellKind = 'dashboard' | 'list' | 'board' | 'document';
/**
 * The page shell of a room (brain-ds.css §3b, docs/ux/desktop-layout-rules.md §2): a `dashboard`, `list`
 * or `board` takes the window's width (up to 1680px); a room without an entry keeps the 1120px document
 * column. Pulse is the worked example (PA63); Tracks is a list page from `v3.8a` (the list, and the track
 * page whose tabs are lists); the Plan board is a board (its object pages, a plan change, stay documents:
 * `shellKind` answers only for a nav item's landing page and a track); the Build console is a dashboard
 * (a run page stays a document); the Workspace is a list page whose thread pages keep the width for the
 * master-detail rail (ThreadRail, from 1440); the Wireframes room is a board — the grid of renders, the stale
 * batch and the page itself (lane 12); the other rooms join as each is laid out for a desktop.
 */
const SHELL_KIND: Partial<Record<NavId, ShellKind>> = {
  pulse: 'dashboard',
  plan: 'board',
  tracks: 'list',
  stories: 'list',
  workspace: 'list',
  build: 'dashboard',
  wireframes: 'board',
};
/** The object pages that share their nav item's shell; every other object page is a document. */
const WIDE_OBJECTS: ReadonlySet<NonNullable<ReturnType<typeof ownerOf>>['object']> = new Set(['track', 'thread', 'wireframe']);
function shellKind(owner: ReturnType<typeof ownerOf>): ShellKind | undefined {
  if (!owner) return undefined;
  if (owner.object && !WIDE_OBJECTS.has(owner.object)) return undefined;
  return SHELL_KIND[owner.nav.id];
}

/**
 * The shell (planning-app.md §3 *Navigation from v3.8*, §4.1; PA62a; the breakpoints of
 * docs/ux/desktop-layout-rules.md §1): one flat route table, one chrome per width chosen in CSS —
 * a 220px nav with the nine items at ≥1024, the same nav as a 56px icon rail with the labels as
 * tooltips at 768–1023, a bottom bar with four of them and *More* under 768. Both are real <nav>
 * landmarks; the hidden one is display:none. The top bar is a breadcrumb whose last crumb is the
 * page's <h1>, then the page's actions at ≥1024 (`./actions`, rules §9.9), then the bar's own buttons.
 */
export function AppShell({ me }: { me: MeResponse }) {
  return (
    <CrumbsProvider>
      <ActionsProvider>
        <AssistProvider me={me}>
          <Shell me={me} />
        </AssistProvider>
      </ActionsProvider>
    </CrumbsProvider>
  );
}

function Shell({ me }: { me: MeResponse }) {
  const intl = useIntl();
  const location = useLocation();
  const [handoff, setHandoff] = useState(false);
  const [more, setMore] = useState(false);
  const assist = useAssistShell();
  // The phone's AI Assist sheet is modal (§3.6): the rest of the shell is inert while it is up; the desktop drawer leaves the page interactive.
  const inert = assist.open && assist.phone ? true : undefined;
  useReportWidget(me);
  const badges = useQuery({ queryKey: ['nav'], queryFn: api.nav, staleTime: 60_000, refetchInterval: 120_000 });
  const counts = badges.data?.badges ?? {};

  const owner = ownerOf(location.pathname);
  const pageCrumbs = usePageCrumbs();
  const crumbs: Crumb[] = atMostThree(pageCrumbs ?? defaultCrumbs(location.pathname, owner, (d) => intl.formatMessage(d)));
  // Provenance is a chip, not a crumb (IA §4.4): `?from=<type>:<id>` after a rail link names the page the reader came from.
  const from = parseFrom(new URLSearchParams(location.search).get('from'));
  const title = crumbs[crumbs.length - 1]?.text ?? intl.formatMessage(common.product);
  useEffect(() => {
    document.title = `${title} · ${intl.formatMessage(common.product)}`;
  }, [title, intl]);
  // Route changes close the sheet (a tap on a row navigates).
  useEffect(() => setMore(false), [location.pathname]);

  const initials = me.person.name
    .split(/\s+/)
    .map((s) => s[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const navTitle = (n: NavItem) => intl.formatMessage(routeTitles[n.path as RoutePath]);
  // IA §1.2: an object URL highlights the item that owns its type (`/decisions/EX1` → Tracks, `/plan/gates/p2` → Plan, `/findings/<id>` → Build console).
  const isCurrentNav = (n: NavItem) => owner?.nav.id === n.id;

  const badge = (path: string, className: string) => {
    const n = counts[path];
    if (!n) return null;
    return (
      <span className={className} aria-label={intl.formatMessage(rooms.badge, { count: n })} data-testid="nav-badge" data-path={path}>
        {intl.formatNumber(n)}
      </span>
    );
  };

  const navRow = (n: NavItem, className: string) => {
    const Icon = NAV_ICONS[n.id];
    return (
      <Link key={n.id} to={n.path} className={`${className}${isCurrentNav(n) ? ' on' : ''}`} aria-current={isCurrentNav(n) ? 'page' : undefined} data-testid="nav-item" data-nav={n.id}>
        <Icon />
        <span className="nav-label">{navTitle(n)}</span>
        {n.shipped ? (
          badge(n.path, 'cnt')
        ) : (
          <span className="soon" aria-label={intl.formatMessage(common.arrivesWith, { package: n.package })} translate="no">
            {n.package.split(' ')[0]}
          </span>
        )}
      </Link>
    );
  };
  return (
    <div className="app" data-assist={assist.open ? (assist.phone ? 'sheet' : 'drawer') : undefined}>
      <a className="skip-link" href="#main" inert={inert}>
        <FormattedMessage {...common.skipToContent} />
      </a>
      <nav className="cp-sidebar" aria-label={intl.formatMessage(rooms.roomsNav)} inert={inert}>
        <NavLink to="/" className="cp-brand" aria-label={intl.formatMessage(common.home)}>
          <BrandMark className="mark" />
          <span>
            <span className="b-name">
              <FormattedMessage {...common.brand} />
            </span>
            <span className="b-prod">
              <FormattedMessage {...common.product} />
            </span>
          </span>
        </NavLink>
        <div className="cp-nav" data-testid="sidebar-nav">
          {NAV.map((n) => navRow(n, 'cp-row'))}
        </div>
        <div className="cp-foot">
          <div className="who">
            <span className="avatar" aria-hidden="true">
              {me.person.avatarUrl ? <img src={me.person.avatarUrl} alt="" /> : initials}
            </span>
            <div>
              <b translate="no">{me.person.name}</b>
              <FormattedMessage {...roles[me.person.role]} />
            </div>
          </div>
          <LocaleSwitcher signedIn compact />
        </div>
      </nav>

      <div className="cp-main" inert={inert}>
        <header className="topbar">
          {/* The phone bar's brand is the mark alone (the critique's `_shell` finding 3: the mark and "Planning" beside
              the title read as two crumbs without a separator); the link's name is the product's, `common.home`. */}
          <NavLink to="/" className="tb-brand" aria-label={intl.formatMessage(common.home)}>
            <BrandMark className="mark" />
          </NavLink>
          <nav className="crumbs" aria-label={intl.formatMessage(rooms.breadcrumb)} data-testid="crumbs">
            <ol>
              {crumbs.map((c, i) => {
                const last = i === crumbs.length - 1;
                // One block per label (`.crumb-text`): a crumb cut for room ends with an ellipsis instead of being clipped
                // on both sides of a centred flex line ("ocume", "Trac" at 390 — the critique's §4 item 2).
                const text = (
                  <span className="crumb-text" translate={c.noTranslate ? 'no' : undefined} lang={c.lang}>
                    {c.text}
                  </span>
                );
                return (
                  <Fragment key={`${i}-${c.text}`}>
                    {i > 0 && (
                      <li className="crumb-sep" aria-hidden="true">
                        ›
                      </li>
                    )}
                    <li className={last ? 'crumb last' : 'crumb'}>{last ? <h1>{text}</h1> : c.to ? <Link to={c.to}>{text}</Link> : text}</li>
                  </Fragment>
                );
              })}
            </ol>
            {from && (
              <Link to={entityPath(from.type, from.id)} className="chip crumb-from" data-testid="crumb-from" data-from={`${from.type}:${from.id}`}>
                <FormattedMessage {...rooms.fromChip} values={{ entity: <span translate="no">{intl.formatMessage(ENTITY_LABEL[from.type], { id: from.id })}</span> }} />
              </Link>
            )}
          </nav>
          <TopbarActionsSlot label={intl.formatMessage(rooms.pageActions)} />
          <span className="spacer" />
          {/* Deployment metadata is for whoever deploys: Pulse's Deployment card shows the full SHA to everyone. */}
          {me.person.role === 'maintainer' && (
            <small className="mono" translate="no" title={intl.formatMessage(rooms.deployedBuild)} data-testid="build-sha">
              {__APP_SHA__.slice(0, 7)}
            </small>
          )}
          <Tooltip text={intl.formatMessage(rooms.openOnDesktop)} align="end" className="tb-handoff" testId="handoff-tooltip">
            <button type="button" className="tb-btn" onClick={() => setHandoff(true)} aria-label={intl.formatMessage(rooms.openOnDesktop)} data-testid="handoff-button">
              <HandoffIcon />
            </button>
          </Tooltip>
          <AssistButton />
        </header>
        <OfflineBanner />
        <main id="main" className="content" tabIndex={-1} data-shell={shellKind(owner)}>
          <Outlet />
        </main>
        {handoff && <LazyHandoffSheet onClose={() => setHandoff(false)} title={title} email={me.person.email} />}
      </div>

      <AssistDrawer crumbs={crumbs.map((c) => c.text).join(' › ')} title={title} />

      <nav className="tabbar" aria-label={intl.formatMessage(rooms.tabsNav)} inert={inert}>
        {PHONE_TABS.map((t) => {
          const Icon = NAV_ICONS[t.id];
          return (
            <Link key={t.id} to={t.path} className={isCurrentNav(t) ? 'on' : undefined} aria-current={isCurrentNav(t) ? 'page' : undefined} data-testid="tab" data-nav={t.id}>
              <Icon />
              {navTitle(t)}
            </Link>
          );
        })}
        <button type="button" className={more || (owner && !PHONE_TABS.some((t) => t.id === owner.nav.id)) ? 'on' : undefined} onClick={() => setMore(true)} aria-haspopup="dialog" aria-expanded={more} data-testid="tab-more">
          <MoreIcon />
          <FormattedMessage {...rooms.more} />
          {Object.keys(counts).length > 0 && <span className="dot" aria-hidden="true" />}
        </button>
      </nav>
      {more && (
        <Sheet title={intl.formatMessage(rooms.more)} description={intl.formatMessage(rooms.moreHelp)} onClose={() => setMore(false)} testId="more-sheet" swipeToClose>
          <div className="more-list" data-testid="more-list">
            {PHONE_MORE.map((n) => navRow(n, 'cp-row more-row'))}
          </div>
        </Sheet>
      )}
    </div>
  );
}

type Format = (d: MessageDescriptor) => string;

/** Nav item › context › object (IA §4.4): three crumbs at most — the owner, the nearest context the page named, the page. */
const atMostThree = (crumbs: Crumb[]): Crumb[] => (crumbs.length <= 3 ? crumbs : [crumbs[0]!, crumbs[1]!, crumbs[crumbs.length - 1]!]);

/** The shell's own crumbs when the page sets none: the owning nav item, the view or tab an object sits under (Plan › Gates, Build console › Findings), the object kind the shell can name. */
function defaultCrumbs(pathname: string, owner: ReturnType<typeof ownerOf>, f: Format): Crumb[] {
  if (!owner) return [{ text: f(common.product) }];
  const out: Crumb[] = [{ text: f(routeTitles[owner.nav.path as RoutePath]), to: owner.nav.path }];
  if (owner.object === 'gate' || pathname.startsWith(GATES_PATH)) out.push({ text: f(routeTitles[GATES_PATH]), to: GATES_PATH });
  if (pathname.startsWith(CALENDAR_PATH)) out.push({ text: f(routeTitles[CALENDAR_PATH]), to: CALENDAR_PATH });
  if (owner.object === 'finding' || pathname.startsWith(FINDINGS_INBOX_PATH)) out.push({ text: f(routeTitles[FINDINGS_INBOX_PATH]), to: FINDINGS_INBOX_PATH });
  const kind = owner.object === 'weeklyStatus' ? rooms.weeklyStatus : owner.object === 'workPackage' ? rooms.workPackage : owner.object === 'translationReview' ? rooms.translationReview : null;
  if (kind) out.push({ text: f(kind) });
  const last = out[out.length - 1]!;
  if (last.to === pathname || out.length === 1) delete last.to;
  return out;
}
